export type StageStatus = { status: string; at?: string; seconds?: number; error?: string; reason?: string; missing?: string[] };
export type Version = { id: string; number: number; status: string; stages: Record<string, StageStatus>; created_at: string; pipeline?: Record<string, unknown> };
export type Recording = { id: string; version: number; sha256: string; bytes?: number; codec?: string; sample_rate?: number; channels?: number; duration_ms: number; acquired_at?: string; resolved_media_url?: string };
export type PublishedTrack = { id: string; position: number; artist: string | null; title: string | null; album: string | null; label: string | null; year: number | null; start_ms: number | null; timing_status: string; marker_kind: string };
export type PublishedMarker = { id: string; kind: string; label: string | null; start_ms: number | null; end_ms: number | null; timing_status: string };
export type Episode = {
  id: string;
  station_name: string;
  station_id: string;
  country: string | null;
  show_title: string | null;
  title: string;
  broadcast_date: string | null;
  languages: string[];
  description: string | null;
  artwork_url: string | null;
  page_url: string | null;
  metadata: { slot?: string; published_duration_ms?: number | null; media_route?: string; rss_note?: string | null; station_name_en?: string | null; show_title_en?: string | null; title_en?: string | null };
  recording: Recording | null;
  version: Version | null;
  counts: { cues?: number; tracks?: number; translations?: number };
  published_track_count: number;
  published_tracks?: PublishedTrack[];
  published_markers?: PublishedMarker[];
  recordings?: Recording[];
  versions?: Version[];
  corrections?: Correction[];
};
export type Correction = { id: string; target_kind: string; field: string; old_value: string | null; new_value: string | null; note: string | null; created_at: string };
export type Translation = { text: string; review_status: string };
export type Cue = { id: string; start_ms: number; end_ms: number; lang: string; text: string; confidence: number | null; flags: string[]; review_status: string; hidden: number; passage_id: string | null; translations: Record<string, Translation> };
export type Track = { id: string; position: number | null; artist: string | null; title: string | null; album: string | null; label: string | null; year: number | null; start_ms: number | null; end_ms: number | null; timing_status: string; identity_status: string; confidence: number | null; evidence: Array<Record<string, unknown>>; flags: string[]; review_status: string; published_track_id: string | null };
export type Region = { id: string; start_ms: number; end_ms: number; kind: string; speech_prob: number | null; music_prob: number | null; singing_prob: number | null; over_music: number; flags: string[] };
export type MusicRegion = { id: string; start_ms: number; end_ms: number; music_prob: number | null; kind?: string };
export type Annotations = { recording: Recording; version: Version | null; speech_regions: Region[]; music_regions: MusicRegion[]; cues: Cue[]; tracks: Track[] };
export type Spectrum = {start_ms:number; end_ms:number; columns:number; bins:number; sample_rate:number; fft_size:number; min_db:number; max_db:number; window:string; values:string};
export type Peaks = { bin_ms: number; duration_ms: number; peaks: number[] };
export type Fixture = { id: string; slot: string; station_name: string; title: string; broadcast_date: string; languages: string[]; imported: boolean };
export type WorkerTask = { id: string; kind: string; status: string; episode_id?: string; fixture_id?: string; error?: string; stages?: string[] | null };
export type WorkerState = { current: WorkerTask | null; progress: { stage?: string; fraction?: number }; queued: WorkerTask[]; history: WorkerTask[] };
export type Health = { worker: boolean; whisper: { ok: boolean; detail: string; model: string }; ollama: { ok: boolean; name?: string; digest?: string; error?: string }; acr: { credentials: boolean; budget: { used: number; ceiling: number; remaining: number } }; ffmpeg: boolean };

async function json<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body.detail ? JSON.stringify(body.detail) : detail;
    } catch {
      /* keep statusText */
    }
    throw new Error(`${res.status} ${detail}`);
  }
  return (await res.json()) as T;
}

const post = (body: unknown) => ({ method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });

export const api = {
  health: () => json<Health>("/api/health"),
  episodes: () => json<Episode[]>("/api/episodes"),
  episode: (id: string) => json<Episode>(`/api/episodes/${id}`),
  annotations: (id: string, includeHidden: boolean) => json<Annotations>(`/api/episodes/${id}/annotations?include_hidden=${includeHidden}`),
  spectrum: (id: string, start: number, end: number, signal?: AbortSignal) => json<Spectrum>(`/api/recordings/${encodeURIComponent(id)}/spectrogram?start_ms=${Math.round(start)}&end_ms=${Math.round(end)}&columns=640`, {signal}),
  peaks: (id: string) => json<Peaks>(`/api/episodes/${id}/peaks`),
  fixtures: () => json<Fixture[]>("/api/fixtures"),
  importFixture: (fixture_id: string) => json<WorkerTask>("/api/episodes/import", post({ fixture_id })),
  process: (id: string, body: { stages?: string[] | null; force?: boolean; new_version?: boolean }) => json<WorkerTask>(`/api/episodes/${id}/process`, post(body)),
  worker: () => json<WorkerState>("/api/worker"),
  whatsNew: () => json<any>("/api/whats-new"),
  correct: (body: { recording_id: string; target_kind: "transcript_cue" | "track_occurrence"; target_id: string; field: string; new_value: string | number | null; note?: string }) => json<Correction>("/api/corrections", post(body)),
  addTrack: (body: { recording_id: string; start_ms: number; end_ms?: number | null; artist?: string; title?: string; album?: string; label?: string; year?: number | null; note?: string }) => json<Track>("/api/tracks", post(body)),
  deleteTrack: (id: string) => json<{ deleted: string }>(`/api/tracks/${id}`, { method: "DELETE" }),
  confirmTrack: (id: string, body: { candidate_index?: number; artist?: string; title?: string; album?: string; label?: string; note?: string }) => json<Track>(`/api/tracks/${id}/confirm`, post(body)),
  audioUrl: (recordingId: string) => `/api/recordings/${recordingId}/audio`,
  exportUrl: (id: string, fmt: "vtt" | "json", lang = "original") => `/api/episodes/${id}/export.${fmt}?lang=${lang}`,
};

export const LANGS: Array<{ code: string; label: string; native: string }> = [
  { code: "en", label: "English", native: "English" },
  { code: "fr", label: "French", native: "Français" },
  { code: "ar", label: "Arabic", native: "العربية" },
  { code: "ja", label: "Japanese", native: "日本語" },
];

export const langLabel = (code: string) => LANGS.find((l) => l.code === code)?.label ?? code;

export function clock(ms: number | null | undefined): string {
  if (ms == null) return "–:––";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`;
}

const LATIN_RE = /^[\p{Script=Latin}\p{N}\p{P}\p{S}\s]*$/u;

/** English-first display names: the English name when the source gives one, otherwise the original; the original
 * is returned separately when it is in another script so the page can show both. */
export function displayNames(ep: Episode): { station: string; show: string; showNative: string | null; title: string; titleNative: string | null } {
  const m = ep.metadata ?? {};
  const show = m.show_title_en || ep.show_title || "";
  const title = m.title_en || ep.title;
  return {
    station: m.station_name_en || ep.station_name,
    show,
    showNative: ep.show_title && show !== ep.show_title && !LATIN_RE.test(ep.show_title) ? ep.show_title : null,
    title,
    titleNative: title !== ep.title && !LATIN_RE.test(ep.title) ? ep.title : null,
  };
}

export function longDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

export const COUNTRY_NAMES: Record<string, string> = { US: "United States", FR: "France", JP: "Japan", GB: "United Kingdom", DE: "Germany", RO: "Romania", NO: "Norway" };

export type Candidate = { artist: string | null; title: string | null; album: string | null; label: string | null; when: string; confidence: number; strength: number; catalogue: { provider?: string; score?: number; title?: string; artist?: string; length_ms?: number; release?: string; release_date?: string; error?: string } | null; length_consistent: boolean | null };

export function candidatesOf(t: Track): Candidate[] {
  const e = t.evidence.find((x) => x.kind === "announcement_candidates_ranked") as { candidates?: Candidate[] } | undefined;
  return e?.candidates ?? [];
}

export function trackName(t: Track): string {
  if (t.artist || t.title) return `${t.artist ?? "?"} — ${t.title ?? "?"}`;
  const c = candidatesOf(t)[0];
  if (c) return `Candidate: ${c.artist ?? "?"} — ${c.title ?? "?"}`;
  return t.timing_status === "sound_segment" ? "Unknown sound" : "Unknown track";
}
